<template>
    <div>
        <div class="day-list-planning" :class="zoomClass">
            <div
                class="week"
                v-for="(week, index) in weeks"
                :key="index"
                :style="weekStyle"
            >
                <div class="day-columns">
                    <div
                        class="day-column"
                        :class="getDayClass(day)"
                        :style="dayStyle"
                        v-for="day in getDays(week)"
                        :key="day.formatted"
                        @click="handleDayClick(day)"
                        @dragover="handleDragOver($event, day)"
                        @drop="handleDrop($event, day)"
                    >
                        <absence-component
                            v-for="absence in getAbsences(day)"
                            :key="day.formatted + absence.id"
                            :absence="absence"
                            :current-zoom="currentZoom"
                        ></absence-component>
                        <div
                            v-for="slot in getSlotsForDay(day)"
                            :key="'slot-' + slot.id"
                            class="day-list-slot slot-info"
                            :class="{
                                'is-dragging': isDragging && draggedSlot?.id === slot.id,
                                'is-preview': isDragging && isSlotInDragGroup(slot) && dragTargetDate && dragTargetDate !== draggedSlot?.date
                            }"
                            :style="getSlotStyle(slot)"
                            draggable="true"
                            @click.stop="openPlanningForm(slot)"
                            @contextmenu.prevent="openPlanningContextMenu($event, slot, day)"
                            @dragstart="handleDragStart($event, slot)"
                            @dragend="handleDragEnd"
                            @mouseover="handleSlotHover($event, slot)"
                        >
                            <div class="slot-edge slot-start" v-if="isFirstSlot(slot)">
                                <div class="dragger" :style="getSlotBorderStyle(slot)"></div>
                            </div>
                            <div class="slot-content">
                                <div class="slot-time">{{ slot.time_start }} - {{ slot.time_end }} <i v-if="slot.repeated_settings_id" class="icofont-retweet"></i></div>
                                <div v-if="getLinkedSlotInfo(slot)" class="slot-date-range">
                                    <span class="date-start">
                                        <i v-if="!getLinkedSlotInfo(slot).isFirst" class="icofont-arrow-left"></i> {{ getLinkedSlotInfo(slot).firstDate }}
                                    </span>
                                    <span class="date-end">
                                        {{ getLinkedSlotInfo(slot).lastDate }} <i v-if="!getLinkedSlotInfo(slot).isLast" class="icofont-arrow-right"></i>
                                    </span>
                                </div>
                                <div
                                    v-for="(line, index) in getSlotInfo(slot)"
                                    :key="'info-' + index"
                                    :class="index === 0 ? 'slot-info-primary' : 'slot-info-secondary'"
                                >
                                    {{ line }}
                                </div>
                                <hr class="slot-divider" v-if="getTeams(slot).length || getPersons(slot).length || getEquipments(slot).length" />
                                <div v-for="team in getTeams(slot)" :key="'t-' + team.id" class="slot-team">
                                    <i class="icofont-people"></i> {{ team.title }}
                                    <div v-for="person in team.persons" :key="'tp-' + person.id" class="slot-team-member">
                                        - <i class="icofont-user"></i> {{ person.name }}
                                    </div>
                                    <div v-for="equipment in team.equipments" :key="'te-' + equipment.id" class="slot-team-member">
                                        - <i class="icofont-vehicle-cement"></i> {{ equipment.title }}
                                    </div>
                                </div>
                                <div v-for="person in getPersons(slot)" :key="'p-' + person.id" class="slot-person">
                                    <i class="icofont-user"></i> {{ person.name }}
                                </div>
                                <div v-for="equipment in getEquipments(slot)" :key="'e-' + equipment.id" class="slot-equipment">
                                    <i class="icofont-vehicle-cement"></i> {{ equipment.title }}
                                </div>
                            </div>
                            <div class="slot-edge slot-end" v-if="isLastSlot(slot)">
                                <div class="dragger" :style="getSlotBorderStyle(slot)"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <Teleport to="body">
            <update-repeated-slots-component
                v-if="updateRepeatedSlotsModal.active"
                :slot_id="updateRepeatedSlotsModal.slot_id"
                :all_update_option="updateRepeatedSlotsModal.all_update_option"
                @close="resetDragChanges"
                @update="updateRepeatedSlots"
            ></update-repeated-slots-component>
        </Teleport>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import {
    DateTimeFormatter,
    DayOfWeek,
    TemporalAdjusters,
} from '@js-joda/core';
import AbsenceComponent from '../AbsenceComponent.vue';
import axios from 'axios';
import { emitter } from '../../../plugins/Mitt';
import { lightenDarkenColor } from '../../../utils/lightenDarkenColor';
import { getSlotStyling } from '../../../utils/planning/getSlotStyling';
import { debounce } from 'lodash';
import UpdateRepeatedSlotsComponent from '../../schedule/widgets/UpdateRepeatedSlotsComponent.vue';

export default {
    components: {
        AbsenceComponent,
        UpdateRepeatedSlotsComponent,
    },
    props: {
        calendarWidth: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            slots: {},
            slotsByDate: {},
            tasks: {},
            teams: {},
            persons: {},
            equipments: {},
            isDragging: false,
            draggedSlot: null,
            dragTargetDate: null,
            debouncedGetData: null,
            updateRepeatedSlotsModal: {
                active: false,
                slot_id: null,
                all_update_option: false,
            },
            optionConfirmedRepeatedSlotsInModal: false,
            updateRepeatedSlotSetting: null,
            pendingDropSlot: null,
            pendingDropTargetDate: null,
        };
    },
    computed: {
        ...mapState('planning', ['dateStart', 'dateEnd', 'defaultWeekschedule', 'companyWeekschedule', 'companyAbsences', 'slotTitles', 'statusesToShow', 'clientsToShow', 'projectsToShow', 'resourcesToShow', 'currentZoom', 'statusColors']),
        zoomClass() {
            return 'zoom-' + this.currentZoom;
        },
        dayStyle() {
            if (this.currentZoom === 0) {
                // Day view: fixed width based on calendarWidth
                const width = this.calendarWidth || window.innerWidth - 40;
                return {
                    flex: `0 0 ${width}px`,
                    minWidth: `${width}px`,
                    maxWidth: `${width}px`,
                };
            }
            if (this.currentZoom === 2) {
                // Month view: days expand within week, 60px minimum
                return {
                    minWidth: '60px',
                };
            }
            // Week view: flex expand with min-width
            return {
                minWidth: '180px',
            };
        },
        weekMinWidth() {
            switch (this.currentZoom) {
                case 0: return null; // Day view - no week container needed
                case 1: return '1260px';
                case 2: return '420px';
                default: return '1260px';
            }
        },
        slotGroups() {
            const groups = {};
            for (const slotId in this.slots) {
                const slot = this.slots[slotId];
                const groupId = slot.first_slot_id;
                if (!groups[groupId]) {
                    groups[groupId] = [];
                }
                groups[groupId].push(parseInt(slotId));
            }
            return groups;
        },
        previewSlotsByDate() {
            // If not dragging or no target, use original
            if (!this.isDragging || !this.draggedSlot || !this.dragTargetDate) {
                return this.slotsByDate;
            }

            const slot = this.draggedSlot;
            const sourceDate = slot.date;
            const targetDate = this.dragTargetDate;

            // If hovering same day, no change
            if (sourceDate === targetDate) {
                return this.slotsByDate;
            }

            // Get all linked slot IDs
            const groupId = slot.first_slot_id;
            const linkedSlotIds = this.slotGroups[groupId] || [slot.id];
            const linkedSlotIdsSet = new Set(linkedSlotIds);

            // Calculate day offset
            const daysDiff = this.getDaysDifference(sourceDate, targetDate);

            // Build new slotsByDate with preview positions
            const result = {};

            // Copy non-affected slots
            for (const date in this.slotsByDate) {
                const slotsForDate = this.slotsByDate[date].filter(
                    id => !linkedSlotIdsSet.has(id)
                );
                if (slotsForDate.length > 0) {
                    result[date] = slotsForDate;
                }
            }

            // Add affected slots at their new dates
            for (const slotId of linkedSlotIds) {
                const slotData = this.slots[slotId];
                if (!slotData) continue;
                const newDate = this.addDays(slotData.date, daysDiff);
                if (!result[newDate]) {
                    result[newDate] = [];
                }
                result[newDate].push(slotId);
            }

            return result;
        },
        weekStyle() {
            if (this.currentZoom === 2) {
                // Month view: 4 weeks fit in viewport
                const weekWidth = this.calendarWidth ? this.calendarWidth / 4 : null;
                return {
                    width: weekWidth ? `${weekWidth}px` : '25%',
                    minWidth: this.weekMinWidth,
                };
            }
            // Day/Week view: 1 week fills viewport
            return {
                width: this.calendarWidth ? `${this.calendarWidth}px` : '100%',
                minWidth: this.weekMinWidth,
            };
        },
        weeks() {
            let dateStart = this.dateStart;
            let dateEnd = this.dateEnd;
            if (!dateStart || !dateEnd) return [];

            let weeks = [];

            // Day view: return individual days, not weeks
            if (this.currentZoom === 0) {
                while (dateStart.isBefore(dateEnd) || dateStart.equals(dateEnd)) {
                    weeks.push(dateStart);
                    dateStart = dateStart.plusDays(1);
                }
                return weeks;
            }

            // Week and month view: iterate by weeks
            while (dateStart.isBefore(dateEnd)) {
                weeks.push(dateStart);
                dateStart = dateStart.plusWeeks(1);
            }
            return weeks;
        },
    },
    methods: {
        getDays(week) {
            const dateformatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');

            // Day view: each "week" is actually just one day
            if (this.currentZoom === 0) {
                return [{
                    localDate: week,
                    formatted: week.format(dateformatter),
                }];
            }

            // Week and month view: return all days of the week
            let days = [];
            let startOfWeek = week;
            const endOfWeek = startOfWeek.with(TemporalAdjusters.nextOrSame(DayOfWeek.SUNDAY));

            days.push({
                localDate: startOfWeek,
                formatted: startOfWeek.format(dateformatter),
            });
            while (startOfWeek.isBefore(endOfWeek)) {
                startOfWeek = startOfWeek.plusDays(1);
                days.push({
                    localDate: startOfWeek,
                    formatted: startOfWeek.format(dateformatter),
                });
            }
            return days;
        },
        getDayClass(day) {
            let weekScheduleDay = this.defaultWeekschedule?.[day.formatted];

            if (this.companyWeekschedule?.[day.formatted]) {
                weekScheduleDay = this.companyWeekschedule[day.formatted];
            }

            if (!weekScheduleDay) return '';
            if (!weekScheduleDay.is_working) return 'not-working';
            if (!weekScheduleDay.is_full_day) return 'not-full';
            return '';
        },
        getWeekschedule(day) {
            let weekScheduleDay = this.defaultWeekschedule?.[day.formatted];

            if (this.companyWeekschedule?.[day.formatted]) {
                weekScheduleDay = this.companyWeekschedule[day.formatted];
            }

            return weekScheduleDay;
        },
        getAbsences(day) {
            let absences = this.companyAbsences?.[day.formatted] ?? {};
            absences = Object.values(absences);
            absences.sort((a, b) => {
                return a.time_start > b.time_start ? 1 : -1;
            });

            const weekschedule = this.getWeekschedule(day);
            if (!weekschedule) {
                return [];
            }

            let posStart = 0;
            absences.map((absence) => {
                let relativeHeight = (absence.duration / weekschedule.duration) * 100;
                if (relativeHeight > 100) {
                    relativeHeight = 100;
                }
                let totalHeight = relativeHeight + posStart;
                if (totalHeight > 100) {
                    relativeHeight = relativeHeight - (totalHeight - 100);
                }
                absence.relativeHeight = relativeHeight;
                absence.posStart = posStart;
                posStart += relativeHeight;
                return absence;
            });

            return absences;
        },
        getData() {
            if (!this.dateStart || !this.dateEnd) return;

            const dateformatter = DateTimeFormatter.ofPattern('yyyy-MM-dd');
            const url = route('planning.day-list-planning.data');

            axios.get(url, {
                params: {
                    date_start: this.dateStart.format(dateformatter),
                    date_end: this.dateEnd.format(dateformatter),
                    filter: {
                        statuses: this.statusesToShow,
                        clients: this.clientsToShow,
                        projects: this.projectsToShow,
                        resources: this.resourcesToShow,
                    },
                },
            }).then((response) => {
                this.slots = response.data.slots;
                this.slotsByDate = response.data.slotsByDate;
                this.tasks = response.data.tasks;
                this.teams = response.data.teams;
                this.persons = response.data.persons;
                this.equipments = response.data.equipments;
            });
        },
        getSlotsForDay(day) {
            const slotIds = this.previewSlotsByDate[day.formatted] || [];
            const slots = slotIds.map((id) => this.slots[id]).filter(Boolean);
            slots.sort((a, b) => a.time_start.localeCompare(b.time_start) || a.id - b.id);
            return slots;
        },
        getTask(slot) {
            return this.tasks[slot.task_id] || {};
        },
        getPersons(slot) {
            return (slot.person_ids || []).map((id) => this.persons[id]).filter(Boolean);
        },
        getEquipments(slot) {
            return (slot.equipment_ids || []).map((id) => this.equipments[id]).filter(Boolean);
        },
        getTeams(slot) {
            return (slot.team_ids || []).map((teamId) => {
                const team = this.teams[teamId];
                if (!team) return null;
                // team_persons is now array of {id, is_team_lead, order} objects (already sorted)
                const teamPersons = slot.team_persons?.[teamId] || [];
                // team_equipments is now array of {id, order} objects (already sorted)
                const teamEquipments = slot.team_equipments?.[teamId] || [];
                return {
                    id: team.id,
                    title: team.title,
                    persons: teamPersons.map((p) => this.persons[p.id]).filter(Boolean),
                    equipments: teamEquipments.map((e) => this.equipments[e.id]).filter(Boolean),
                };
            }).filter(Boolean);
        },
        getSlotStyle(slot) {
            const localStore = {
                state: {
                    planning: {
                        mode: 'project',
                        statusColors: this.statusColors,
                        tasks: this.tasks,
                        persons: this.persons,
                        equipments: this.equipments,
                    },
                },
            };

            const mappedSlot = {
                taskId: slot.task_id,
                persons: Object.fromEntries(
                    (slot.person_ids || []).map(id => [id, this.persons[id]])
                ),
                equipments: Object.fromEntries(
                    (slot.equipment_ids || []).map(id => [id, this.equipments[id]])
                ),
                teams: Object.fromEntries(
                    (slot.team_ids || []).map(id => [id, this.teams[id]])
                ),
            };

            const styling = getSlotStyling(localStore, mappedSlot);
            return {
                backgroundColor: styling.backgroundColor,
                color: styling.color,
                backgroundImage: styling.backgroundImage,
            };
        },
        getSlotInfo(slot) {
            const task = this.getTask(slot);
            const project = task.project;
            const client = task.client;
            const lines = [];

            for (const setting in this.slotTitles) {
                if (!this.slotTitles[setting]) continue;

                let text = null;

                if (setting === 'task') {
                    text = task.title;
                } else if (setting === 'project') {
                    text = project?.title || trans('no project');
                } else if (setting === 'client') {
                    text = client?.title || '-';
                } else if (setting === 'project_internal_number') {
                    if (project?.internal_id) {
                        text = project.internal_id;
                    } else if (!this.slotTitles['project']) {
                        text = project?.title || trans('no project');
                    }
                }

                if (text) {
                    lines.push(text);
                }
            }

            return lines;
        },
        openPlanningForm(slot) {
            emitter.emit('open-planning-form', {
                slotId: slot.id,
            });
        },
        openPlanningContextMenu(event, slot, day) {
            const mappedSlot = {
                id: slot.id,
                taskId: slot.task_id,
                firstOfGroup: this.isFirstSlot(slot),
                lastOfGroup: this.isLastSlot(slot),
                firstSlotId: slot.first_slot_id,
                date: day.formatted,
                timeStart: slot.time_start,
                timeEnd: slot.time_end,
                persons: this.getPersons(slot),
                teams: this.getTeams(slot),
                equipments: this.getEquipments(slot),
            };
            emitter.emit('show-context-menu-component', {
                eventObject: { slot: mappedSlot },
                event: event,
            });
        },
        getLinkedSlotInfo(slot) {
            const groupId = slot.first_slot_id;
            const linkedSlotIds = this.slotGroups[groupId] || [slot.id];

            // Only show for multi-slot groups
            if (linkedSlotIds.length <= 1) return null;

            const linkedSlots = linkedSlotIds.map(id => this.slots[id]).filter(Boolean);
            const dates = linkedSlots.map(s => s.date).sort();
            const firstDate = dates[0];
            const lastDate = dates[dates.length - 1];

            return {
                isFirst: slot.date === firstDate,
                isLast: slot.date === lastDate,
                firstDate: this.formatShortDate(firstDate),
                lastDate: this.formatShortDate(lastDate),
            };
        },
        formatShortDate(dateStr) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        },
        isSlotInDragGroup(slot) {
            if (!this.draggedSlot) return false;
            const groupId = this.draggedSlot.first_slot_id;
            const linkedSlotIds = this.slotGroups[groupId] || [];
            return linkedSlotIds.includes(slot.id);
        },
        handleDayClick(day) {
            if (this.isDragging) return;

            const weekScheduleDay = this.getWeekschedule(day);

            emitter.emit('open-planning-form', {
                dateStart: day.formatted,
                dateEnd: day.formatted,
                timeStart: weekScheduleDay?.time_start || '08:00',
                timeEnd: weekScheduleDay?.time_end || '16:00',
            });
        },
        handleDragStart(event, slot) {
            document.body.classList.add('cursor-grabbing');
            this.isDragging = true;
            this.draggedSlot = slot;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', slot.id.toString());
        },
        handleDragEnd() {
            document.body.classList.remove('cursor-grabbing');
            this.isDragging = false;
            this.draggedSlot = null;
            this.dragTargetDate = null;
        },
        clearPendingDrop() {
            this.pendingDropSlot = null;
            this.pendingDropTargetDate = null;
        },
        resetDragChanges() {
            this.updateRepeatedSlotsModal.active = false;
            this.optionConfirmedRepeatedSlotsInModal = false;
            this.updateRepeatedSlotSetting = null;
            this.clearPendingDrop();
            this.handleDragEnd();
        },
        updateRepeatedSlots(value) {
            this.updateRepeatedSlotSetting = value;
            this.optionConfirmedRepeatedSlotsInModal = true;
            this.updateRepeatedSlotsModal.active = false;
            // Re-trigger the drop - pendingDropSlot and pendingDropTargetDate are already set
            this.handleDrop(null, null);
        },
        handleDragOver(event, day) {
            if (!this.isDragging) return;
            event.preventDefault();

            // Update target date for preview
            if (this.dragTargetDate !== day.formatted) {
                this.dragTargetDate = day.formatted;
            }
        },
        handleDrop(event, targetDay) {
            if (event) event.preventDefault();

            // Use pending slot if resuming from modal, otherwise use draggedSlot
            const slot = this.pendingDropSlot || this.draggedSlot;
            if (!slot) return;

            const sourceDate = slot.date;
            const targetDate = targetDay ? targetDay.formatted : this.pendingDropTargetDate;

            if (sourceDate === targetDate) {
                this.clearPendingDrop();
                this.handleDragEnd();
                return;
            }

            // Check if slot is repeated and modal not yet confirmed
            if (slot.repeated_settings_id && !this.optionConfirmedRepeatedSlotsInModal) {
                // Store info for after modal confirms
                this.pendingDropSlot = slot;
                this.pendingDropTargetDate = targetDate;
                this.updateRepeatedSlotsModal.active = true;
                this.updateRepeatedSlotsModal.slot_id = slot.id;
                return;
            }

            // Get all linked slots
            const groupId = slot.first_slot_id;
            const linkedSlotIds = this.slotGroups[groupId] || [slot.id];
            const linkedSlots = linkedSlotIds.map(id => this.slots[id]).filter(Boolean);

            // Calculate date offset
            const daysDiff = this.getDaysDifference(sourceDate, targetDate);

            // Calculate new date range for all linked slots
            const newDates = linkedSlots.map(s => this.addDays(s.date, daysDiff)).sort();
            const newDateStart = newDates[0];
            const newDateEnd = newDates[newDates.length - 1];

            // Optimistic update: Update local data immediately to prevent snap-back
            const linkedSlotIdsSet = new Set(linkedSlotIds);

            // Update slotsByDate - remove from old dates, add to new dates
            const newSlotsByDate = {};
            for (const date in this.slotsByDate) {
                const slotsForDate = this.slotsByDate[date].filter(
                    id => !linkedSlotIdsSet.has(id)
                );
                if (slotsForDate.length > 0) {
                    newSlotsByDate[date] = slotsForDate;
                }
            }
            for (const slotId of linkedSlotIds) {
                const slotData = this.slots[slotId];
                if (!slotData) continue;
                const newDate = this.addDays(slotData.date, daysDiff);
                if (!newSlotsByDate[newDate]) {
                    newSlotsByDate[newDate] = [];
                }
                newSlotsByDate[newDate].push(slotId);
            }
            this.slotsByDate = newSlotsByDate;

            // Update slot dates in slots object
            for (const slotId of linkedSlotIds) {
                if (this.slots[slotId]) {
                    this.slots[slotId] = {
                        ...this.slots[slotId],
                        date: this.addDays(this.slots[slotId].date, daysDiff),
                    };
                }
            }

            this.moveSlots(slot, newDateStart, newDateEnd);
            this.handleDragEnd();
        },
        getDaysDifference(date1, date2) {
            const d1 = new Date(date1);
            const d2 = new Date(date2);
            const diffTime = d2.getTime() - d1.getTime();
            return Math.round(diffTime / (1000 * 60 * 60 * 24));
        },
        addDays(dateStr, days) {
            const date = new Date(dateStr);
            date.setDate(date.getDate() + days);
            return date.toISOString().split('T')[0];
        },
        moveSlots(slot, dateStart, dateEnd) {
            const groupId = slot.first_slot_id;
            const linkedSlotIds = this.slotGroups[groupId] || [slot.id];
            const linkedSlots = linkedSlotIds.map(id => this.slots[id]).filter(Boolean);

            // Collect individual person/equipment IDs (those NOT in teams)
            const allPersonIds = [...new Set(linkedSlots.flatMap(s => s.person_ids || []))];
            const allEquipmentIds = [...new Set(linkedSlots.flatMap(s => s.equipment_ids || []))];

            // Build assignedTeams from slot team data
            const allTeams = {};
            for (const s of linkedSlots) {
                for (const teamId of (s.team_ids || [])) {
                    if (!allTeams[teamId]) {
                        allTeams[teamId] = { persons: {}, equipments: {} };
                    }
                    // Add team persons (team_persons[teamId] is array of {id, is_team_lead, order})
                    const teamPersons = s.team_persons?.[teamId] || [];
                    for (const p of teamPersons) {
                        allTeams[teamId].persons[p.id] = true;
                    }
                    // Add team equipments (team_equipments[teamId] is array of {id, order})
                    const teamEquipments = s.team_equipments?.[teamId] || [];
                    for (const e of teamEquipments) {
                        allTeams[teamId].equipments[e.id] = true;
                    }
                }
            }

            const url = route('planning.schedule-slots', slot.id);

            axios.post(url, {
                taskId: slot.task_id,
                timings: {
                    dateStart: dateStart,
                    dateEnd: dateEnd,
                    timeStart: slot.time_start,
                    timeEnd: slot.time_end,
                },
                assignees: {
                    assignedTeams: allTeams,
                    assignedPersons: allPersonIds,
                    assignedEquipments: allEquipmentIds,
                    linkedAssignees: true,
                },
                updateRepeatedSlotSetting: this.updateRepeatedSlotSetting,
            }).then(() => {
                this.getData();
                this.clearPendingDrop();
                if (this.optionConfirmedRepeatedSlotsInModal) {
                    this.optionConfirmedRepeatedSlotsInModal = false;
                    this.updateRepeatedSlotSetting = null;
                }
            }).catch((error) => {
                console.error('Failed to move slots:', error);
            });
        },
        handleSlotHover(event, slot) {
            if (this.isDragging) {
                return;
            }
            emitter.emit('show-slot-hover-info', {
                slotId: slot.first_slot_id ?? slot.id,
                event: event,
            });
        },
        getSlotBorderStyle(slot) {
            const localStore = {
                state: {
                    planning: {
                        mode: 'project',
                        statusColors: this.statusColors,
                        tasks: this.tasks,
                        persons: this.persons,
                        equipments: this.equipments,
                    },
                },
            };

            const mappedSlot = {
                taskId: slot.task_id,
                persons: Object.fromEntries(
                    (slot.person_ids || []).map(id => [id, this.persons[id]])
                ),
                equipments: Object.fromEntries(
                    (slot.equipment_ids || []).map(id => [id, this.equipments[id]])
                ),
                teams: Object.fromEntries(
                    (slot.team_ids || []).map(id => [id, this.teams[id]])
                ),
            };

            const styling = getSlotStyling(localStore, mappedSlot);
            return {
                backgroundColor: styling.darkenBackgroundColor,
            };
        },
        isFirstSlot(slot) {
            const info = this.getLinkedSlotInfo(slot);
            return info ? info.isFirst : true;
        },
        isLastSlot(slot) {
            const info = this.getLinkedSlotInfo(slot);
            return info ? info.isLast : true;
        },
    },
    watch: {
        dateStart() {
            this.debouncedGetData();
        },
        dateEnd() {
            this.debouncedGetData();
        },
        statusesToShow() {
            this.debouncedGetData();
        },
        clientsToShow() {
            this.debouncedGetData();
        },
        projectsToShow() {
            this.debouncedGetData();
        },
        resourcesToShow() {
            this.debouncedGetData();
        },
    },
    created() {
        this.debouncedGetData = debounce(this.getData, 50);
        this.debouncedGetData();
    },
    mounted() {
        this.handlePlanningSlots = () => {
            this.getData();
        };
        emitter.on('planning-slots-changed', this.handlePlanningSlots);
    },
    beforeUnmount() {
        emitter.off('planning-slots-changed', this.handlePlanningSlots);
        if (this.debouncedGetData) {
            this.debouncedGetData.cancel();
        }
    },
};
</script>

<style lang="scss" scoped>
.day-list-planning {
    display: inline-flex;
    height: calc(100vh - 100px);
    border-top: 1px solid #ccc;
}
.week {
    flex: none;
    box-sizing: border-box;
    border-left: 1px solid #6e7c7c;
    border-right: 1px solid #6e7c7c;
    margin-left: -1px;
    margin-right: -1px;
    &:last-child {
        border-right: 0;
    }
}
.day-columns {
    display: flex;
    width: 100%;
    min-height: 100%;
    box-sizing: border-box;
}
.day-column {
    flex: 1;
    box-sizing: border-box;
    border-right: 1px solid #ccc;
    background: #fff;
    position: relative;
    padding-bottom: 20px;
    &:hover {
        cursor: pointer;
        -webkit-filter: brightness(95%);
        filter: brightness(95%);
    }
    &:last-child {
        border-right: 0;
    }
}
.not-working {
    background: #aaa;
}
.not-full:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: #aaa;
    clip-path: polygon(100% 0, 100% 100%, 0 100%);
}
.day-list-slot {
    position: relative;
    z-index: 1;
    cursor: pointer;
    margin: 4px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 1.4;
    overflow: hidden;
    display: flex;
    &:hover {
        -webkit-filter: brightness(95%);
        filter: brightness(95%);
    }
    &.is-dragging {
        opacity: 0.5;
    }
    &.is-preview {
        opacity: 0.7;
        border-style: dashed;
    }
    &:active {
        cursor: grabbing;
    }

    .slot-content {
        flex: 1;
        min-width: 0;
        padding: 6px;
        pointer-events: none;
    }

    .slot-edge {
        flex-shrink: 0;
        pointer-events: none;
        .dragger {
            width: 5px;
            height: 100%;
        }
    }

    .slot-start .dragger {
        border-radius: 5px 0 0 5px;
    }

    .slot-end .dragger {
        border-radius: 0 5px 5px 0;
    }

    .slot-time,
    .slot-info-primary,
    .slot-info-secondary,
    .slot-team-member,
    .slot-person,
    .slot-equipment {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        pointer-events: none;
    }

    .slot-team,
    .slot-date-range,
    .slot-divider {
        pointer-events: none;
    }

    .slot-time {
        font-size: 12px;
    }
    .slot-date-range {
        display: flex;
        justify-content: space-between;
        font-size: 10px;
        opacity: 0.8;
        .date-start {
            text-align: left;
        }
        .date-end {
            text-align: right;
            margin-left: auto;
        }
    }
    .slot-info-primary {
        font-weight: bold;
    }
    .slot-info-secondary {
        font-size: 10px;
    }
    .slot-divider {
        margin: 4px 0;
        border: 0;
        border-top: 1px solid rgba(0, 0, 0, 0.2);
        width: 100%;
    }
    .slot-team,
    .slot-person,
    .slot-equipment {
        font-size: 11px;
        i {
            margin-right: 3px;
        }
    }
    .slot-team-member {
        font-size: 10px;
        padding-left: 4px;
    }
}

// Zoom level specific styles
// Day view (zoom-0): day sizing handled by inline dayStyle
.day-list-planning.zoom-0 {
    .week {
        flex: 0 0 auto;
    }
}

// Week view (zoom-1) and Month view (zoom-2):
// day sizing handled by inline dayStyle, week sizing by weekStyle
</style>
